import type { Location } from '@/api/resources/locations'
import type { Machine } from '@/api/resources/machines'
import { useQuery } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useRouteContext,
} from '@tanstack/react-router'
import { locationsQueryOptions } from '@/api/resources/locations'
import { machinesQueryOptions } from '@/api/resources/machines'
import { organisationQueryOptions } from '@/api/resources/organisations'
import { LoadingSpinner } from '@/components/feedback/loading-spinner'
import { LocationMachineGroup } from '@/components/machines/location-machine-group'
import { MachineCard } from '@/components/machines/machine-card'
import { OrganisationDetailHeader } from '@/components/organisations/organisation-detail-header'
import { hasMachineWarning } from '@/lib/machines'

export const Route = createFileRoute('/_app/organisations/$organisationId')({
  head: () => ({ meta: [{ title: 'Organisation - MyJunea' }] }),
  beforeLoad: async ({ params, context }) => {
    const organisationId = Number(params.organisationId)
    if (Number.isNaN(organisationId)) {
      throw redirect({ to: '/' })
    }

    const organisation = await context.queryClient.ensureQueryData(
      organisationQueryOptions(organisationId),
    )

    return { organisation }
  },
  component: OrganisationDetailLayout,
})

interface LocationGroup {
  groupKey: string
  locationId: number
  locationName: string
  machines: Machine[]
}

function buildLocationGroups(locations: Location[], machines: Machine[]): LocationGroup[] {
  const groups = new Map<string, LocationGroup>()

  for (const location of locations) {
    groups.set(String(location.id), {
      groupKey: String(location.id),
      locationId: location.id,
      locationName: location.name,
      machines: [],
    })
  }

  for (const machine of machines) {
    const locationId = machine.location?.id
    if (locationId === undefined) continue
    const groupKey = String(locationId)
    const existing = groups.get(groupKey)
    if (existing !== undefined) {
      existing.machines.push(machine)
    }
  }

  return Array.from(groups.values()).sort((a, b) =>
    a.locationName.toLowerCase().localeCompare(b.locationName.toLowerCase()),
  )
}

function OrganisationDetailLayout(): React.ReactElement {
  const { organisationId } = Route.useParams()
  const { organisation: contextOrganisation } = useRouteContext({
    from: '/_app/organisations/$organisationId',
  })
  const navigate = useNavigate()

  const organisationIdNum = Number(organisationId)

  const { data: organisation } = useQuery({
    ...organisationQueryOptions(organisationIdNum),
    initialData: contextOrganisation,
  })

  const { data: machines, isLoading: isMachinesLoading } = useQuery(
    machinesQueryOptions({ organisation_id: [organisationIdNum] }),
  )

  const { data: locations, isLoading: isLocationsLoading } = useQuery(
    locationsQueryOptions({ organisation_id: [organisationIdNum] }),
  )

  const isLoading = isMachinesLoading || isLocationsLoading

  const locationGroups =
    machines !== undefined && locations !== undefined
      ? buildLocationGroups(locations, machines)
      : []

  const handleEditOrganisation = (): void => {
    void navigate({
      to: '/organisations/$organisationId/edit',
      params: { organisationId },
      resetScroll: false,
    })
  }

  return (
    <div className="space-y-6">
      <OrganisationDetailHeader organisation={organisation} onEditClick={handleEditOrganisation} />

      <h2 className="text-lg font-bold tracking-tight">Locations</h2>

      {isLoading && <LoadingSpinner />}

      {!isLoading && locationGroups.length === 0 && (
        <p className="text-muted-foreground py-10 text-center">No locations or machines found.</p>
      )}

      {!isLoading && locationGroups.length > 0 && (
        <div className="space-y-4">
          {locationGroups.map((group) => (
            <LocationMachineGroup
              key={group.groupKey}
              organisationName={organisation.name}
              locationName={group.locationName}
              machineCount={group.machines.length}
              hasWarning={group.machines.some(hasMachineWarning)}
              onEditClick={() => {
                void navigate({
                  to: '/organisations/$organisationId/locations/$locationId/edit',
                  params: {
                    organisationId,
                    locationId: String(group.locationId),
                  },
                  resetScroll: false,
                })
              }}
            >
              {group.machines.map((machine) => (
                <Link
                  key={machine.code}
                  to="/machines/$machineCode"
                  params={{ machineCode: machine.code }}
                  className="focus-visible:ring-ring rounded-md transition-transform hover:scale-[1.02] focus-visible:ring-2 focus-visible:outline-none"
                >
                  <MachineCard
                    name={machine.name}
                    code={machine.code}
                    type={machine.type}
                    typeName={machine.type_name}
                    status={machine.status}
                    statusCategory={machine.status_category}
                    stock={machine.stock}
                    capacity={machine.capacity}
                    emptySlots={machine.empty_slots}
                    className="h-full"
                  />
                </Link>
              ))}
            </LocationMachineGroup>
          ))}
        </div>
      )}

      <Outlet />
    </div>
  )
}
